import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const BACKEND = BACKEND_URL;
// Must match backend ALLOWED_WIDTHS / snap_width: smallest allowed variant >= requested, capped at the largest.
export const ALLOWED_WIDTHS = [160, 400, 800];
export const snapWidth = (w) => ALLOWED_WIDTHS.find((a) => a >= w) ?? ALLOWED_WIDTHS[ALLOWED_WIDTHS.length - 1];

const http = axios.create({ baseURL: API });
http.interceptors.request.use((config) => {
  const token = localStorage.getItem("qora_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const FIELD_IT = { owner_password: "Password", owner_email: "Email proprietario", owner_name: "Nome proprietario", name: "Nome", email: "Email", password: "Password", company_name: "Nome attività", slug: "Slug", title: "Titolo", url: "URL", target_value: "Destinazione", price: "Prezzo" };
const MSG_IT = (x) => {
  if (x.type === "string_too_short") return `troppo corto (minimo ${x.ctx?.min_length} caratteri)`;
  if (x.type === "missing") return "obbligatorio";
  if (/email/i.test(x.type || "") || /email/i.test(x.msg || "")) return "indirizzo email non valido";
  if (x.type?.startsWith("string_pattern") || x.type?.startsWith("value_error")) return "valore non valido";
  return x.msg || "valore non valido";
};
export const errorMessage = (err, fallback = "Operazione non riuscita. Riprova.") => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string" && d) return d;
  if (Array.isArray(d)) return d.map((x) => { const f = x.loc?.[x.loc.length - 1]; return `${FIELD_IT[f] || f || "Campo"}: ${MSG_IT(x)}`; }).join(" · ");
  if (d && typeof d === "object") return d.message || d.msg || JSON.stringify(d);
  if (err?.response?.status === 403) return "Non hai i permessi o il servizio non è attivo sul tuo piano.";
  if (err?.response?.status === 404) return "Risorsa non trovata.";
  if (err?.response?.status >= 500) return "Errore del server. Riprova tra qualche istante.";
  if (err?.request && !err?.response) return "Connessione non riuscita. Controlla la rete e riprova.";
  return fallback;
};
// Normalize every API error so callers can always show e.response.data.detail as a readable string.
http.interceptors.response.use((r) => r, (err) => {
  const msg = errorMessage(err);
  if (!err.response) err.response = { status: 0, data: {} };
  if (!err.response.data || typeof err.response.data !== "object") err.response.data = {};
  err.response.data.detail = msg;
  err.qoraMessage = msg;
  if (err.response.status === 401 && localStorage.getItem("qora_token") && !/\/auth\//.test(err.config?.url || "")) {
    localStorage.removeItem("qora_token");
    if (!window.location.pathname.startsWith("/login")) window.location.assign("/login?expired=1");
  }
  return Promise.reject(err);
});

export const api = {
  // auth
  register: (data) => http.post("/auth/register", data),
  login: (data) => http.post("/auth/login", data),
  me: () => http.get("/auth/me"),
  config: () => http.get("/config"),
  // company
  company: () => http.get("/company"),
  staff: () => http.get("/company/staff"),
  addStaff: (data) => http.post("/company/staff", data),
  delStaff: (id) => http.delete(`/company/staff/${id}`),
  // admin
  adminCompanies: () => http.get("/admin/companies"),
  adminCreateCompany: (data) => http.post("/admin/companies", data),
  adminUpdateCompany: (id, data) => http.patch(`/admin/companies/${id}`, data),
  adminDeleteCompany: (id) => http.delete(`/admin/companies/${id}`),
  adminStats: () => http.get("/admin/stats"),
  // commercial
  catalog: () => http.get("/catalog"),
  myServices: () => http.get("/services"),
  validateCoupon: (data) => http.post("/services/validate-coupon", data),
  activateServices: (data) => http.post("/services/activate", data),
  adminCoupons: () => http.get("/admin/coupons"),
  adminCreateCoupon: (data) => http.post("/admin/coupons", data),
  adminUpdateCoupon: (id, data) => http.patch(`/admin/coupons/${id}`, data),
  adminGrant: (data) => http.post("/admin/grant", data),
  adminRevoke: (data) => http.post("/admin/revoke", data),
  // qr
  qrList: () => http.get("/qrcodes"),
  qrCreate: (data) => http.post("/qrcodes", data),
  qrGet: (id) => http.get(`/qrcodes/${id}`),
  qrUpdate: (id, data) => http.patch(`/qrcodes/${id}`, data),
  qrDuplicate: (id) => http.post(`/qrcodes/${id}/duplicate`),
  qrDelete: (id) => http.delete(`/qrcodes/${id}`),
  qrAnalytics: (id, params) => http.get(`/qrcodes/${id}/analytics`, { params }),
  qrAnalyticsCsvUrl: (id, params) => `${API}/qrcodes/${id}/analytics/export.csv?${new URLSearchParams(params)}`,
  analyticsOverview: (params) => http.get("/analytics/overview", { params }),
  analyticsCsvUrl: (params) => `${API}/analytics/export.csv?${new URLSearchParams(params)}`,
  // public demo links (real seeded content)
  publicDemo: () => http.get("/public/demo"),
  // billing
  billingPlans: () => http.get("/billing/plans"),
  slotState: () => http.get("/billing/slot/state", { withCredentials: true }),
  slotSpin: () => http.post("/billing/slot/spin", {}, { withCredentials: true }),
  slotAttach: () => http.post("/billing/slot/attach", {}, { withCredentials: true }),
  billingQuote: (d) => http.post("/billing/quote", d, { withCredentials: true }),
  billingProfile: () => http.get("/billing/profile"),
  billingProfileSave: (d) => http.put("/billing/profile", d),
  billingCheckout: (d) => http.post("/billing/checkout", d, { withCredentials: true }),
  billingCheckoutStatus: (sid) => http.get(`/billing/checkout/status/${sid}`),
  billingSubscription: () => http.get("/billing/subscription"),
  billingPortal: (d) => http.post("/billing/portal", d),
  billingEvent: (name, meta = {}) => http.post("/billing/events", { name, meta }),
  // seo (superadmin)
  seoOverview: () => http.get("/seo/overview"),
  seoSettings: () => http.get("/seo/settings"),
  seoSettingsSave: (d) => http.put("/seo/settings", d),
  seoIndexNowKey: () => http.post("/seo/indexnow/key"),
  seoIndexNowSubmit: (paths = []) => http.post("/seo/indexnow/submit", { paths }),
  seoIndexNowLog: () => http.get("/seo/indexnow/log"),
  seoCrawlerHits: () => http.get("/seo/crawler-hits"),
  seoContentList: () => http.get("/seo/content"),
  seoContentGet: (id) => http.get(`/seo/content/${id}`),
  seoContentCreate: (d) => http.post("/seo/content", d),
  seoContentUpdate: (id, d) => http.put(`/seo/content/${id}`, d),
  seoContentDelete: (id) => http.delete(`/seo/content/${id}`),
  // seo public
  seoPublicSettings: () => http.get("/public/seo/settings"),
  seoPublicContent: (params) => http.get("/public/seo/content", { params }),
  seoPublicArticle: (slug) => http.get(`/public/seo/content/${slug}`),
  seoHit: (path) => http.post("/public/seo/hit", { path }),
  seoPageModel: (path) => http.get(`/public/seo/pages${path}`),
  billingAdminConfig: () => http.get("/billing/admin/config"),
  // pages
  pageList: () => http.get("/pages"),
  pageCreate: (data) => http.post("/pages", data),
  pageGet: (id) => http.get(`/pages/${id}`),
  pageUpdate: (id, data) => http.patch(`/pages/${id}`, data),
  pageDelete: (id) => http.delete(`/pages/${id}`),
  publicPage: (slug) => http.get(`/public/pages/${slug}`),
  // minisite engine
  msMeta: () => http.get("/minisite/meta"),
  msTemplates: () => http.get("/minisite/templates"),
  msRecommend: (d) => http.post("/minisite/recommend", d),
  msApplyTemplate: (pid, d) => http.post(`/minisite/pages/${pid}/apply-template`, d),
  msDowngradePreview: (pid) => http.get(`/minisite/pages/${pid}/downgrade-preview`),
  msStyleCheck: (style) => http.post("/minisite/style-check", { style }),
  msAnalytics: (pid, days = 30) => http.get(`/minisite/pages/${pid}/analytics`, { params: { days } }),
  msLeads: (pid) => http.get(`/minisite/pages/${pid}/leads`),
  msAdminTemplates: () => http.get("/minisite/admin/templates"),
  msAdminTemplateCreate: (d) => http.post("/minisite/admin/templates", d),
  msAdminTemplateUpdate: (id, d) => http.put(`/minisite/admin/templates/${id}`, d),
  msAdminTemplateDuplicate: (id) => http.post(`/minisite/admin/templates/${id}/duplicate`),
  msAdminTemplateDelete: (id) => http.delete(`/minisite/admin/templates/${id}`),
  msAdminBlocks: () => http.get("/minisite/admin/blocks"),
  msAdminBlockUpdate: (t, d) => http.put(`/minisite/admin/blocks/${t}`, d),
  msAdminRules: () => http.get("/minisite/admin/rules"),
  msAdminRulesUpdate: (d) => http.put("/minisite/admin/rules", d),
  // menus
  menuList: () => http.get("/menus"),
  menuCreate: (data) => http.post("/menus", data),
  menuGet: (id) => http.get(`/menus/${id}`),
  menuUpdate: (id, data) => http.patch(`/menus/${id}`, data),
  menuDelete: (id) => http.delete(`/menus/${id}`),
  publicMenu: (id) => http.get(`/public/menus/${id}`),
  menuPdf: (id, download) => http.get(`/menus/${id}/pdf`, { params: { download: download ? 1 : 0 }, responseType: "blob" }),
  uploadMedia: (formData, onProgress) => http.post("/media", formData, { headers: { "Content-Type": "multipart/form-data" }, onUploadProgress: onProgress }),
  deleteMedia: (id) => http.delete(`/media/${id}`),
  mediaUrl: (id, w) => `${API}/media/${id}${w ? `?w=${snapWidth(w)}` : ""}`,
  // promotions
  promoList: () => http.get("/promotions"),
  promoCreate: (d) => http.post("/promotions", d),
  promoUpdate: (id, d) => http.patch(`/promotions/${id}`, d),
  promoDuplicate: (id) => http.post(`/promotions/${id}/duplicate`),
  promoDelete: (id) => http.delete(`/promotions/${id}`),
  publicPromotions: (params) => http.get("/public/promotions", { params }),
  // ai
  allergenCatalog: () => http.get("/ai/allergens/catalog"),
  aiAllergens: (data) => http.post("/ai/allergens", data),
  aiText: (data) => http.post("/ai/text", data),
  aiImport: (formData) => http.post("/ai/import", formData, { headers: { "Content-Type": "multipart/form-data" } }),
  aiImportStatus: (jobId) => http.get(`/ai/import/${jobId}`),
};

// QR scan URL (dynamic codes resolve through backend redirect).
// Base is centralized/configurable (prod domain e.g. https://myqora.it) and falls back to current origin.
let QR_BASE = typeof window !== "undefined" ? window.location.origin : BACKEND;
export const setQrBase = (b) => { QR_BASE = b || (typeof window !== "undefined" ? window.location.origin : BACKEND); };
// ---- Menu Check (public, anonymous session header) ----
export const mcSession = async () => {
  let t = localStorage.getItem("qora_mc_session");
  if (!t) { const { data } = await http.post("/public/menu-check/session"); t = data.session; localStorage.setItem("qora_mc_session", t); }
  return t;
};
const mcH = async (extra = {}) => ({ headers: { "X-MC-Session": await mcSession(), ...extra } });
export const menuCheck = {
  upload: async (files, onProgress) => { const fd = new FormData(); files.forEach((f) => fd.append("files", f)); return http.post("/public/menu-check/upload", fd, { ...(await mcH({ "Content-Type": "multipart/form-data" })), onUploadProgress: (e) => onProgress?.(Math.round((e.loaded * 100) / (e.total || 1))), timeout: 180000 }); },
  analyze: async (id) => http.post(`/public/menu-check/${id}/analyze`, {}, { ...(await mcH()), timeout: 200000 }),
  get: async (id) => http.get(`/public/menu-check/${id}`, await mcH()),
  list: async () => http.get("/public/menu-check", await mcH()),
  remove: async (id) => http.delete(`/public/menu-check/${id}`, await mcH()),
  lead: async (d) => http.post("/public/menu-check/leads", d, await mcH()),
  event: async (name, meta = {}) => http.post("/public/menu-check/events", { name, meta }, await mcH()).catch(() => {}),
  claim: (session) => http.post("/menu-check/claim", { session }),
  badge: (menuId) => http.get(`/public/menu-check/badge/menu/${menuId}`),
  myVerifications: () => http.get("/menu-check/verifications"),
  admin: {
    list: (name) => http.get(`/admin/menu-check/${name}`), create: (name, d) => http.post(`/admin/menu-check/${name}`, d), update: (name, id, d) => http.patch(`/admin/menu-check/${name}/${id}`, d), remove: (name, id) => http.delete(`/admin/menu-check/${name}/${id}`),
    leadUpdate: (id, d) => http.patch(`/admin/menu-check/leads/${id}`, d), menus: () => http.get("/admin/menu-check/menus"), verifications: () => http.get("/admin/menu-check/verifications"), createVerification: (d) => http.post("/admin/menu-check/verifications", d), verificationStatus: (id, d) => http.patch(`/admin/menu-check/verifications/${id}/status`, d),
    checks: () => http.get("/admin/menu-check/checks"), settings: () => http.get("/admin/menu-check/settings"), patchSettings: (d) => http.patch("/admin/menu-check/settings", d), funnel: () => http.get("/admin/menu-check/funnel"), report: () => http.get("/admin/menu-check/report"),
  },
};
export const printkit = {
  options: () => http.get("/printkit/options"), generate: (d) => http.post("/printkit/generate", d), remove: (id) => http.delete(`/printkit/${id}`),
  fileUrl: (id, fmt, out = "png", download = 0) => `${API}/printkit/${id}/file?fmt=${fmt}&out=${out}&download=${download}${download ? "" : "&dpi=110"}`,
  admin: { settings: () => http.get("/admin/printkit/settings"), patch: (d) => http.patch("/admin/printkit/settings", d), kits: () => http.get("/admin/printkit/kits") },
};
export const authedBlob = async (url) => { const r = await http.get(url.replace(API, ""), { responseType: "blob" }); return URL.createObjectURL(r.data); };
export const scanUrl = (code) => `${QR_BASE}/api/r/${code}`;

export default http;
